import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { useState } from "react";
import type { Paginated } from "@rn/contracts";
import { PageBody, PageHeader } from "@/components/crm/AppShell";
import { TaskDialog, TaskList, type Task } from "@/components/crm/TaskList";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api } from "@/lib/api";

export const Route = createFileRoute("/_authenticated/tarefas")({
  component: TarefasPage,
});

type Summary = { pending: number; overdue: number; today: number };
type Scope = "mine" | "all" | "unassigned";

const DUE_POR_ABA: Record<string, string> = {
  abertas: "",
  atrasadas: "overdue",
  hoje: "today",
  semana: "week",
};

/** "3" ao lado da aba, só quando há o que contar. */
function Contagem({ n }: { n: number | undefined }) {
  if (!n) return null;
  return <span className="kpi ml-1.5 text-[11px] text-muted-foreground">{n}</span>;
}

/**
 * Tarefas.
 *
 * As abas são recortes de prazo, não de status: a pergunta de quem abre esta
 * tela é "o que eu tenho para fazer agora", e nenhuma resposta a isso começa
 * escolhendo entre pendente e concluída.
 *
 * Os limites de "hoje" e "atrasada" são calculados no servidor, no fuso do
 * tenant. Fazer isso no navegador daria respostas diferentes para quem abre a
 * tela viajando, e nenhuma delas seria a do calendário da clínica. A lista
 * só agrupa visualmente o que o servidor devolveu.
 */
function TarefasPage() {
  const [scope, setScope] = useState<Scope>("mine");
  const [due, setDue] = useState<string>("");
  const [criando, setCriando] = useState(false);

  const summary = useQuery({
    queryKey: ["tasks", "summary"],
    queryFn: () => api.get<Summary>("/tasks/summary"),
  });

  const queryKey = ["tasks", "list", scope, due];
  const tasks = useQuery({
    queryKey,
    queryFn: () =>
      api.get<Paginated<Task>>("/tasks", {
        scope,
        due: due || undefined,
        pageSize: 100,
      }),
  });

  const descricao = summary.data
    ? [
        `${summary.data.pending} ${summary.data.pending === 1 ? "pendente" : "pendentes"}`,
        summary.data.overdue > 0
          ? `${summary.data.overdue} ${summary.data.overdue === 1 ? "atrasada" : "atrasadas"}`
          : null,
      ]
        .filter(Boolean)
        .join(" · ")
    : "O que precisa acontecer, e quando";

  return (
    <>
      <PageHeader
        title="Tarefas"
        description={descricao}
        action={
          <Button onClick={() => setCriando(true)}>
            <Plus />
            Nova tarefa
          </Button>
        }
      >
        <Tabs variant="pills" defaultValue="mine" onValueChange={(v) => setScope(v as Scope)}>
          <TabsList>
            <TabsTrigger value="mine">Minhas</TabsTrigger>
            <TabsTrigger value="all">De todos</TabsTrigger>
            <TabsTrigger value="unassigned">Sem responsável</TabsTrigger>
          </TabsList>
        </Tabs>

        <Tabs
          variant="pills"
          defaultValue="abertas"
          onValueChange={(v) => setDue(DUE_POR_ABA[v] ?? "")}
        >
          <TabsList>
            <TabsTrigger value="abertas">Todas</TabsTrigger>
            <TabsTrigger value="atrasadas">
              Atrasadas
              <Contagem n={summary.data?.overdue} />
            </TabsTrigger>
            <TabsTrigger value="hoje">
              Hoje
              <Contagem n={summary.data?.today} />
            </TabsTrigger>
            <TabsTrigger value="semana">7 dias</TabsTrigger>
          </TabsList>
        </Tabs>
      </PageHeader>

      <PageBody className="mx-auto w-full max-w-4xl">
        <TaskList
          tasks={tasks.data?.data ?? []}
          loading={tasks.isLoading}
          queryKey={queryKey}
          grouped
          composer={false}
        />
      </PageBody>

      <TaskDialog open={criando} onOpenChange={setCriando} queryKey={queryKey} />
    </>
  );
}
